import { WidgetManifestEntry, WidgetPageConfig } from '@/models/widget';
import { GENERAL_DOCS } from '@/pages/widgetEditor/docs/generalDocs';
import {
    buildEntryScriptTag,
    buildWidgetDoc,
    DerivedAttribute,
} from '@/pages/widgetEditor/utils';

/**
 * Rules an AI assistant gets wrong without being told. Each one is a real
 * failure: a self-hosted bundle loses its API bridge, a second entry script
 * loads a second store, and `sample-data` would show demo data to customers.
 */
/** General sections about this editor's own UI, which a customer page never has. */
const EDITOR_ONLY_SECTIONS = ['editor', 'sampleData'];

const IMPLEMENTATION_RULES = [
    'Das Skript genau einmal pro Seite einbinden, unverändert von der angegebenen URL. Die Widget-Dateien nicht kopieren, bündeln oder selbst hosten: die Widgets erreichen die Starjack-API nur über ihre eigene Herkunft.',
    '`<starjack-config>` genau einmal pro Seite einsetzen. Kein Widget bekommt Integration-Token oder Lift-Operator als eigenes Attribut.',
    'Nur die hier dokumentierten Attribute und Events verwenden. Attributwerte sind immer Strings.',
    'Design-Attribute nur setzen, wenn sie vom Standardwert abweichen. Für eine andere Schriftart als den Standard das Google-Fonts-Stylesheet per `<link>` laden, bevor das Skript eingebunden wird.',
    'Das Attribut `sample-data` ist nur für die Vorschau im Editor gedacht und darf nie auf eine Kundenseite.',
    'Am einfachsten ist der Einbettungscode aus dem Starjack Widget-Editor: er enthält Skript, Konfiguration, Schriftarten und Design bereits fertig.',
];

/** Table cells must stay on one line and must not open a new column. */
const cell = (value: string | undefined): string =>
    value ? value.replace(/\|/g, '\\|').replace(/\s*\n\s*/g, ' ') : '-';

const table = (head: string[], rows: string[][]): string =>
    [
        `| ${head.join(' | ')} |`,
        `| ${head.map(() => '---').join(' | ')} |`,
        ...rows.map((row) => `| ${row.map(cell).join(' | ')} |`),
    ].join('\n');

const codeBlock = (code: string, lang = 'html'): string =>
    `\`\`\`${lang}\n${code}\n\`\`\``;

const attributeTable = (attributes: DerivedAttribute[]): string =>
    table(
        ['Attribut', 'Typ', 'Standard', 'Anpassbar', 'Beschreibung'],
        attributes.map((attribute) => [
            `\`${attribute.attribute}\``,
            attribute.options?.length
                ? attribute.options.join(', ')
                : attribute.type,
            attribute.default,
            attribute.adjustable ? 'Design' : undefined,
            attribute.description,
        ])
    );

const widgetMarkdown = (
    widget: WidgetManifestEntry,
    entryUrl?: string
): string => {
    const doc = buildWidgetDoc(widget, entryUrl);
    const parts: string[] = [
        `### ${doc.label} (\`<${widget.tag}>\`)`,
        doc.renderMode ? `Darstellung: ${doc.renderMode}` : '',
    ];

    const authored = doc.documentation;
    if (authored) {
        parts.push(authored.summary);
        if (authored.usage) parts.push(authored.usage);
        if (authored.requires?.length) {
            parts.push(
                '**Voraussetzungen**',
                authored.requires.map((item) => `- ${item}`).join('\n')
            );
        }
        const steps = [
            ['Einbindung', authored.integration],
            ['Anpassung', authored.adjustment ?? []],
        ] as const;
        steps.forEach(([title, list]) => {
            if (list.length === 0) return;
            parts.push(`#### ${title}`);
            list.forEach((step, index) => {
                parts.push(`${index + 1}. **${step.title}**: ${step.body}`);
                if (step.code) parts.push(codeBlock(step.code));
                if (step.parameters?.length) {
                    parts.push(
                        `Attribute: ${step.parameters
                            .map(
                                (name) =>
                                    `\`${doc.attributesByName[name]?.attribute ?? name}\``
                            )
                            .join(', ')}`
                    );
                }
            });
        });
    }

    parts.push(
        '#### Attribute',
        doc.attributes.length
            ? attributeTable(doc.attributes)
            : 'Dieses Widget hat keine Attribute.'
    );

    if (doc.events.length) {
        parts.push('#### Events');
        doc.events.forEach(({ name, event }) => {
            parts.push(
                `- \`${name}\`${
                    event.handlerProp
                        ? ` (Handler-Eigenschaft \`${event.handlerProp}\`)`
                        : ''
                }${event.description ? `: ${event.description}` : ''}`
            );
            if (event.detail && Object.keys(event.detail).length) {
                parts.push(
                    table(
                        [
                            '`event.detail`-Feld',
                            'Typ',
                            'Optional',
                            'Beschreibung',
                        ],
                        Object.entries(event.detail).map(([field, value]) => [
                            `\`${field}\``,
                            value.type,
                            value.nullable ? 'ja' : 'nein',
                            value.description,
                        ])
                    )
                );
            }
        });
    }

    return parts.filter(Boolean).join('\n\n');
};

/**
 * The whole documentation as one Markdown document, for pasting into an AI
 * assistant. Built from the same manifest and `buildWidgetDoc` as the dialog,
 * so the two cannot say different things.
 */
export const buildDocsMarkdown = (
    widgets: WidgetManifestEntry[],
    entryUrl?: string,
    pageConfig?: WidgetPageConfig
): string => {
    const quickStart = [
        ...(entryUrl ? [buildEntryScriptTag(entryUrl)] : []),
        ...(pageConfig
            ? [
                  `<${pageConfig.tag}\n${Object.values(pageConfig.parameters)
                      .map((param) => `    ${param.attribute}="…"`)
                      .join('\n')}\n></${pageConfig.tag}>`,
              ]
            : []),
        ...(widgets[0] ? [`<${widgets[0].tag}></${widgets[0].tag}>`] : []),
    ].join('\n');

    return [
        '# Starjack-Widgets einbinden',
        'Starjack-Widgets sind HTML-Elemente für Ticketverkauf, Warenkorb, Checkout und Kundenkonto, die auf jeder Website laufen. Dieses Dokument beschreibt alle Widgets einer Version vollständig und ist aus dem Widget-Manifest erzeugt.',
        '## Regeln für die Umsetzung',
        IMPLEMENTATION_RULES.map((rule) => `- ${rule}`).join('\n'),
        '## Schnellstart',
        quickStart ? codeBlock(quickStart) : '',
        ...(pageConfig
            ? [
                  `## Seitenkonfiguration \`<${pageConfig.tag}>\``,
                  pageConfig.description,
                  attributeTable(
                      Object.entries(pageConfig.parameters).map(
                          ([name, param]) => ({ name, ...param })
                      )
                  ),
              ]
            : []),
        '## Grundlagen',
        ...GENERAL_DOCS.filter(
            (section) => !EDITOR_ONLY_SECTIONS.includes(section.id)
        ).flatMap((section) => [
            `### ${section.title}`,
            section.body,
            section.code ? codeBlock(section.code, 'js') : '',
        ]),
        '## Widgets',
        ...widgets.map((widget) => widgetMarkdown(widget, entryUrl)),
    ]
        .filter(Boolean)
        .join('\n\n')
        .concat('\n');
};

/**
 * The same document as an Agent Skill (`SKILL.md`), so a coding assistant
 * (Claude Code, Cursor, …) loads it on its own whenever someone asks it to
 * embed a Starjack widget.
 */
export const buildDocsSkill = (markdown: string): string =>
    [
        '---',
        'name: starjack-widgets',
        'description: Embeds and configures Starjack widgets (ticket shop, product filter, cart, checkout, customer account, login) on a website. Use when adding a <starjack-*> element, the Starjack widget script or <starjack-config> to a page, styling a Starjack widget, or listening to its starjack:* events.',
        '---',
        '',
        markdown,
    ].join('\n');
